import { useState } from 'react';
import type { z } from 'zod';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@moneyskyline/ui';
import { AmountText } from '@/components/AmountText';
import { formatMoney, useMoneyFormat } from '@/lib/money';
import type { businessMonthSchema } from '@/api/hooks/useBusinessYear';

type Month = z.infer<typeof businessMonthSchema>;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function barHeight(value: string | null, max: number): number {
  if (value == null || max <= 0) return 0;
  const n = Math.abs(Number(value));
  if (!Number.isFinite(n) || n === 0) return 0;
  return Math.max(4, Math.round((n / max) * 100));
}

function monthUnavailableCopy(month: Month, chartUnavailable: boolean): string | null {
  if (chartUnavailable) return 'Year totals aren’t available. This is not a $0 month.';
  if (month.state === 'not_elapsed') return 'Has not elapsed. This is not a $0 month.';
  if (month.income == null && month.expense == null) return 'Totals for this month aren’t available. This is not a $0 month.';
  return null;
}

function asExpense(value: string): string {
  if (value === '0' || value === '0.0000' || value.startsWith('-')) return value;
  return `-${value}`;
}

function MonthTooltipBody({
  month,
  currency,
  chartUnavailable,
}: {
  month: Month;
  currency: string;
  chartUnavailable?: boolean;
}) {
  const label = MONTH_NAMES[month.month - 1];
  const unavailableCopy = monthUnavailableCopy(month, Boolean(chartUnavailable));
  if (unavailableCopy) {
    return (
      <div className="max-w-xs text-sm">
        <div className="font-semibold">{label}</div>
        <p className="mt-1 text-muted-foreground">{unavailableCopy}</p>
      </div>
    );
  }
  return (
    <div className="min-w-[11rem] max-w-xs text-sm" data-testid="year-chart-tooltip">
      <div className="font-semibold">
        {label}
        {month.state === 'partial' ? <span className="ml-1 font-normal text-muted-foreground">· partial</span> : null}
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-4">
        <span className="text-muted-foreground">Income</span>
        <AmountText value={month.income} currency={currency} />
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-muted-foreground">Expense</span>
        <AmountText value={month.expense == null ? null : asExpense(month.expense)} currency={currency} />
      </div>
      {month.expenses.length > 0 ? (
        <div className="mt-2 border-t border-border pt-2">
          <div className="mb-1 text-[12px] text-muted-foreground">Expense types</div>
          {month.expenses.map((row) => (
            <div key={row.label} className="flex items-center justify-between gap-4 py-0.5">
              <span>
                {row.label}
                {row.item_count != null ? <span className="text-[12px] text-muted-foreground"> · {row.item_count}</span> : null}
              </span>
              <AmountText value={row.amount} currency={currency} />
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-[12px] text-muted-foreground">No expenses this month.</p>
      )}
    </div>
  );
}

export function YearChart({
  months,
  unavailable,
  currency = 'USD',
}: {
  months?: Month[];
  unavailable?: boolean;
  currency?: string;
}) {
  const { masked } = useMoneyFormat();
  const [pinned, setPinned] = useState<number | null>(null);
  const series = months ?? Array.from({ length: 12 }, (_, i) => ({
    month: i + 1,
    state: 'not_elapsed' as const,
    income: null,
    expense: null,
    expenses: [],
  }));
  const max = Math.max(
    1,
    ...series.flatMap((m) => [m.income, m.expense].map((v) => (v == null ? 0 : Math.abs(Number(v))))),
  );
  const pinnedMonth = pinned != null ? series.find((m) => m.month === pinned) : undefined;

  return (
    <TooltipProvider delayDuration={200}>
      <div data-testid="business-year-chart">
        <p className="sr-only">
          {unavailable
            ? 'Year bars unavailable. Future months are hatched, never zero.'
            : 'Monthly income and expense bars. Hover or tap a month for amounts and expense types. Hatched months have not elapsed.'}
        </p>
        <div className="relative mt-4 flex h-[150px] items-end gap-1.5 border-b border-border">
          {series.map((month) => {
            const hatched = Boolean(unavailable) || month.state === 'not_elapsed' || (month.income == null && month.expense == null);
            const name = MONTH_NAMES[month.month - 1];
            const gapCopy = monthUnavailableCopy(month, Boolean(unavailable));
            const ariaLabel = gapCopy
              ? `${name}. ${gapCopy}`
              : `${name}. Income ${formatMoney(month.income, { currency, masked })}. Expense ${formatMoney(month.expense == null ? null : asExpense(month.expense), { currency, masked })}.`;
            return (
              <Tooltip key={month.month}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className={`flex h-full min-h-11 flex-1 items-end rounded-sm ${hatched ? 'ms-hatch self-stretch' : 'gap-0.5'}`}
                    aria-label={ariaLabel}
                    aria-pressed={pinned === month.month}
                    title=""
                    onClick={() => setPinned((current) => (current === month.month ? null : month.month))}
                  >
                    {hatched ? null : (
                      <>
                        <span
                          className={`flex-1 rounded-sm bg-money-positive ${month.state === 'partial' ? 'opacity-50' : ''}`}
                          style={{ height: `${barHeight(month.income, max)}%` }}
                        />
                        <span
                          className={`flex-1 rounded-sm bg-foreground ${month.state === 'partial' ? 'opacity-50' : ''}`}
                          style={{ height: `${barHeight(month.expense, max)}%` }}
                        />
                      </>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="px-3 py-2">
                  <MonthTooltipBody month={month} currency={currency} chartUnavailable={unavailable} />
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
        <div className="mt-1.5 flex justify-between text-[12px] text-muted-foreground">
          <span>Jan</span>
          <span>Jul</span>
          <span>Dec</span>
        </div>
        <div className="mt-1.5 flex flex-wrap gap-3 text-[12px] text-muted-foreground">
          <span><span className="mr-1 inline-block h-2 w-2 bg-money-positive" /> income</span>
          <span><span className="mr-1 inline-block h-2 w-2 bg-foreground" /> expense</span>
          <span>hatched = not yet</span>
        </div>
        {pinnedMonth ? (
          <div className="mt-3 rounded-[10px] border border-border bg-card p-3" data-testid="year-chart-month-detail">
            <MonthTooltipBody month={pinnedMonth} currency={currency} chartUnavailable={unavailable} />
          </div>
        ) : null}
      </div>
    </TooltipProvider>
  );
}

export default YearChart;
